import { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { InvoiceContentSelectionFormType, InvoiceFormProps } from "models/propTypes/invoicePropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { InvoiceFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { WintFormSetValues } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region  components
import { useWintForm, useWintWizard, WintForm, WizardFormSectionContainer } from "components/common/wintForms";
import CustomerSelectionStep from "../_fields/customerSelectionStep";
import ContentSelectionStep from "../_fields/contentSelectionStep";
import OtherSettingsStep from "../_fields/otherSettingsStep";
import RutRotSelectionStep from "../_fields/rutRotSelectionStep";
import RecurringConfigurationStep from "../_fields/recurringConfigurationStep";
import InvoiceCreateTemplatePreview from "../../pdfPreview/createTemplatePreview";
import InvoiceWizardQuickSelection from "../_fields/wizardQuickSelection";
import CreateInvoiceFooter from "../../forms/create/footer";
import RecurringConfigurationStepFields from "../_fields/recurringConfigurationFields";
import CustomerInformationLoader from "../_fields/customerInformationLoader";
import useForwardedCosts from "./costForwarding/useForwardedCosts";
import { ActionLinkButton } from "components/common";
import { TranslationKeys } from "translations";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const InvoiceForm: ForwardRefRenderFunction<FormHandles, InvoiceFormProps> = (props, ref) => {
  const { isRecurring, hasROT, hasRUT, invoice, showOnlyRecurringFields, additionalImages } = props;

  const { wintFormProps, formState, connect, values, errors, touched, submitted, setValues } =
    useWintForm<InvoiceFormTypes.Create>({
      formRef: ref,
      defaultValues: props.defaultValues,
      validationSchema: props.validationSchema,
      onFormValidationChange: props.onFormValidationChange,
      validateOnChange: false,
      validateOnMount: true,
      validateOnBlur: true,
      enableReinitialize: false,
    });

  const wizard = useWintWizard(formState);

  const savedInvoice = invoice as WintSuperkollAPIModel.Invoice | undefined;
  // Recurring invoices and credit notes never carry forwarded costs.
  const forwardedCosts = useForwardedCosts(
    values,
    setValues,
    formState.setFieldValue,
    !isRecurring && !showOnlyRecurringFields && savedInvoice?.creditedInvoiceId === undefined
  );

  return (
    <WintForm {...wintFormProps}>
      <CustomerInformationLoader
        invoiceId={invoice?.id}
        values={values}
        connect={connect}
        setValues={setValues}
        lastSavedSendProperties={invoice && "invoiceSendInfo" in invoice ? invoice.invoiceSendInfo : undefined}
        revenueAccountsEnabled={props.revenueAccountsEnabled}
        creditedInvoiceId={(invoice as WintSuperkollAPIModel.Invoice)?.creditedInvoiceId}
      />
      {showOnlyRecurringFields ? (
        <RecurringConfigurationStepFields connect={connect} values={values} touched={touched} invoice={invoice} />
      ) : (
        <>
          <InvoiceWizardQuickSelection connect={connect} values={values} isRecurring={isRecurring} invoice={invoice} />
          <InvoiceCreateTemplatePreview
            values={values}
            isRecurring={isRecurring}
            invoice={invoice}
            additionalImages={additionalImages}
          />
          <WizardFormSectionContainer wizardProps={wizard}>
            <CustomerSelectionStep
              connect={connect}
              values={values}
              submitted={submitted}
              setValues={setValues}
              isQuotation={false}
              wizardProps={wizard}
              revenueAccountsEnabled={props.revenueAccountsEnabled}
              creditedInvoiceId={(invoice as WintSuperkollAPIModel.Invoice)?.creditedInvoiceId}
            />
            <ContentSelectionStep
              connect={connect}
              values={values}
              errors={errors}
              touched={touched}
              submitted={submitted}
              wizardProps={wizard}
              isRecurring={isRecurring}
              invoiceId={invoice?.id}
              setValues={setValues as WintFormSetValues<InvoiceContentSelectionFormType>}
              revenueAccountsEnabled={props.revenueAccountsEnabled}
              additionalRowActions={
                forwardedCosts.canForwardCosts ? (
                  <ActionLinkButton onClick={forwardedCosts.openDialog} testId="addForwardedCostsAction">
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ForwardedCostsAdd)}
                  </ActionLinkButton>
                ) : undefined
              }
            />
            {!(hasROT || hasRUT) || isRecurring ? undefined : (
              <RutRotSelectionStep
                connect={connect}
                values={values}
                errors={errors}
                touched={touched}
                submitted={submitted}
                hasROT={hasROT}
                hasRUT={hasRUT}
                wizardProps={wizard}
              />
            )}
            <OtherSettingsStep
              connect={connect}
              values={values}
              submitted={submitted}
              isRecurring={isRecurring}
              wizardProps={wizard}
              creditedInvoiceId={(props.invoice as WintSuperkollAPIModel.Invoice)?.creditedInvoiceId}
              pendingAttachments={forwardedCosts.pendingAttachments}
            />

            {isRecurring ? (
              <RecurringConfigurationStep
                connect={connect}
                values={values}
                touched={touched}
                submitted={submitted}
                wizardProps={wizard}
                invoice={invoice}
              />
            ) : undefined}
          </WizardFormSectionContainer>

          <CreateInvoiceFooter
            isRecurring={isRecurring}
            form={formState}
            invoice={invoice}
            reloadInvoice={props.reloadInvoice}
            wizard={wizard}
            revenueAccountsEnabled={props.revenueAccountsEnabled}
            forwardedCosts={forwardedCosts}
          />
        </>
      )}
    </WintForm>
  );
};

export default forwardRef(InvoiceForm);
